import type { Schemas } from '@argzombie/api-client';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { PageHeader } from '../components/PageHeader';
import { PlayerBasesSection } from '../components/PlayerBasesSection';
import { PlayerSavePoints } from '../components/PlayerSavePoints';
import { PlayerStoreSection } from '../components/PlayerStoreSection';
import { PlayerEffectsSection } from '../components/PlayerEffectsSection';
import { PlayerSkillsSection } from '../components/PlayerSkillsSection';
import { PlayerActivitySection } from '../components/PlayerActivitySection';
import { PlayerStorySection } from '../components/PlayerStorySection';
import { ResetProgressButton } from '../components/quick/ResetProgressButton';
import { PlayerFeaturesSection } from '../components/PlayerFeaturesSection';
import { PlayerList } from '../components/PlayerList';
import { PlayerTriggerSection } from '../components/PlayerTriggerSection';
import { GrantItemPicker } from '../components/GrantItemPicker';
import { TeamsPanel } from '../components/TeamsPanel';
import { TestAccountSwitch } from '../components/TestAccountSwitch';
import { QuickActionsBar } from '../components/quick/QuickActionsBar';
import { ConditionsTable } from '../components/ConditionsTable';
import { PlayerCharactersTable } from '../components/PlayerCharactersTable';
import { unwrap, useApi } from '../lib/api';
import {
  RunScopeContext,
  characterLabel,
  characterName,
  defaultCharacter,
  statusWord,
  withRunId,
  type AdminPlayerDetail,
  type AdminRaidRow,
} from '../lib/characters';
import { useDraft } from '../lib/draft';
import { kgLabel, poolOf, restLabel, squadSummary } from '../lib/playerSupport';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';

/** The list lives in `components/PlayerList.tsx` (admin tools: paging, test accounts hidden). */
export function PlayersPage() {
  return <PlayerList />;
}

function Stacks({ title, stacks }: { title: string; stacks: Schemas['ItemStack'][] }) {
  return (
    <div>
      <h3 className="text-xs font-semibold tracking-wide text-bruise uppercase">{title}</h3>
      {stacks.length === 0 ? (
        <p className="text-sm text-bruise">empty</p>
      ) : (
        <ul className="font-mono text-sm">
          {stacks.map((s) => (
            <li key={s.itemId}>
              {s.itemId} ×{s.quantity}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type RaidRow = AdminRaidRow;

/** "Kitchen knife (auto) · M9", the active slot marked with ▸. Names come from the local draft when there is one. */
function formatLoadout(
  loadout: RaidRow['loadout'],
  name: (id: string) => string = (id) => id,
): string {
  if (!loadout) return '-';
  const slot = (id: string | null | undefined, auto: boolean | undefined, n: number) => {
    const label = id ? `${name(id)}${auto ? ' (auto)' : ''}` : 'fists';
    return (loadout.activeSlot ?? 1) === n ? `▸${label}` : label;
  };
  return `${slot(loadout.primaryId, loadout.primaryAuto, 1)} · ${slot(loadout.secondaryId, loadout.secondaryAuto, 2)}`;
}

/** "m9, ammo_9mm×20": a compact list of what the raid carried in. */
function formatStacks(stacks: RaidRow['carried']): string {
  if (!stacks?.length) return '-';
  return stacks
    .map((s) => ((s.quantity ?? 1) > 1 ? `${s.itemId}×${s.quantity}` : s.itemId))
    .join(', ');
}

export function PlayerDetailPage({ playerId }: { playerId: string }) {
  const { client: plainClient, session } = useApi();
  const qc = useQueryClient();
  // The character the run-scoped sections read (ADR 0069). Unset = the API's default, the active character.
  const [runId, setRunId] = useState<string | undefined>();
  const client = withRunId(plainClient, runId);
  const detail = useQuery({
    queryKey: ['admin-player', playerId, runId],
    queryFn: async (): Promise<AdminPlayerDetail> =>
      unwrap(await client.GET('/admin/players/{id}', { params: { path: { id: playerId } } })),
    placeholderData: keepPreviousData,
  });

  const draft = useDraft();
  const itemNames = new Map((draft?.bundle.items ?? []).map((i) => [i.id ?? '', i.name ?? '']));
  const itemName = (id: string) => itemNames.get(id) || id;

  const [grant, setGrant] = useState({ location: 'base', itemId: '', quantity: 1, reason: '' });
  const [grantError, setGrantError] = useState<string | null>(null);
  const doGrant = useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST('/admin/players/{id}/grant', {
          params: { path: { id: playerId } },
          body: grant,
        }),
      ),
    onSuccess: () => {
      setGrantError(null);
      setGrant({ ...grant, reason: '' });
      void qc.invalidateQueries({ queryKey: ['admin-player', playerId] });
    },
    onError: (e) => setGrantError(String(e)),
  });

  if (detail.isPending) return <p>Loading&hellip;</p>;
  if (detail.isError) return <p className="text-blam">{String(detail.error)}</p>;
  const d = detail.data;
  const characters = d.characters ?? [];
  const character = characters.find((c) => c.runId === runId) ?? defaultCharacter(characters);
  const characterOf = (r: RaidRow) => characters.find((c) => c.runId === r.runId);
  const teamList = d.teams?.teams ?? [];
  const ownTeam = teamList.find((t) => t.pack.location === 'carried');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    doGrant.mutate();
  };

  return (
    <>
      <PageHeader
        title={d.player.displayName}
        description={`${d.player.email ?? ''} · roles ${d.roles.join(', ')} · trust ${d.player.trustScore}`}
      />
      {d.characters && (
        <PlayerCharactersTable
          characters={characters}
          selectedRunId={character?.runId}
          onSelect={setRunId}
        />
      )}
      <RunScopeContext.Provider value={{ runId, character }}>
        <QuickActionsBar
          playerId={playerId}
          extraActions={<ResetProgressButton playerId={playerId} />}
        />
        <TestAccountSwitch player={d.player} />
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded border-2 border-ink bg-white p-4">
            <h2 className="font-semibold">{character ? characterLabel(character) : 'Run'}</h2>
            {d.run ? (
              <p className="text-sm">
                #{d.run.number} {statusWord(d.run.status)}, day {d.run.daysSurvived},{' '}
                {d.run.rest ? `rest: ${restLabel(d.run.rest)}` : `${d.run.health}/${d.run.maxHealth} HP`}
                {d.run.atHome ? ', at home' : ''}
                {d.run.causeOfDeath ? ` (${d.run.causeOfDeath})` : ''}
                {d.run.assisted && (
                  <span className="ml-1 rounded bg-caption px-1 font-semibold">assisted</span>
                )}
              </p>
            ) : (
              <p className="text-sm">No active character.</p>
            )}
            <p className="mt-1 text-sm">
              Home: {d.home ? `${d.home.lat.toFixed(5)}, ${d.home.lon.toFixed(5)}` : 'not set'}
            </p>
            <p className="text-sm">
              Survivors: {d.survivors.map((s) => `${s.name} (${s.roleId})`).join(', ') || 'none'}
            </p>
            {d.inventory && (
              <div className="mt-3 grid grid-cols-3 gap-3">
                <Stacks
                  title={`Carried materials ${kgLabel(d.inventory.carriedKg, d.inventory.carryCapacityKg)}`}
                  stacks={d.inventory.carried}
                />
                <Stacks title="Arrived home" stacks={d.inventory.arrivedHome} />
                <Stacks
                  title={`Inventory (base, shared pool) ${kgLabel(d.inventory.baseKg, d.inventory.baseCapacityKg)}`}
                  stacks={d.inventory.base}
                />
              </div>
            )}
          </section>

          <section className="rounded border-2 border-ink bg-white p-4">
            <h2 className="font-semibold">Grant or revoke</h2>
            {!session.isAdmin && <p className="text-sm text-bruise">Admins only.</p>}
            <form onSubmit={submit} className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <select
                aria-label="Location"
                value={grant.location}
                onChange={(e) => setGrant({ ...grant, location: e.target.value })}
                className="rounded border-2 border-ink px-2 py-1"
              >
                <option value="carried">
                  carried{ownTeam ? ` (Team ${ownTeam.slot}'s materials)` : ' (materials)'}
                </option>
                <option value="arrivedHome">arrived home</option>
                <option value="base">base (Inventory, the shared pool)</option>
                {teamList
                  .filter((t) => t.pack.location !== 'carried')
                  .map((t) => (
                    <option key={t.id} value={t.pack.location}>
                      Team {t.slot} materials ({t.leader.name})
                    </option>
                  ))}
              </select>
              <input
                aria-label="Item id"
                placeholder="item id"
                value={grant.itemId}
                onChange={(e) => setGrant({ ...grant, itemId: e.target.value })}
                className="rounded border-2 border-ink px-2 py-1 font-mono"
              />
              <GrantItemPicker
                itemId={grant.itemId}
                location={grant.location}
                quantity={grant.quantity}
                onPick={(itemId) => setGrant({ ...grant, itemId })}
              />
              <input
                aria-label="Quantity"
                type="number"
                value={grant.quantity}
                onChange={(e) => setGrant({ ...grant, quantity: Number(e.target.value) })}
                className="rounded border-2 border-ink px-2 py-1"
              />
              <input
                aria-label="Reason"
                placeholder="reason (required)"
                value={grant.reason}
                onChange={(e) => setGrant({ ...grant, reason: e.target.value })}
                className="rounded border-2 border-ink px-2 py-1"
              />
              <button
                type="submit"
                disabled={!grant.reason || !grant.itemId || doGrant.isPending}
                className="col-span-2 rounded border-2 border-ink bg-caption px-3 py-1 font-semibold disabled:opacity-50"
              >
                {grant.quantity < 0 ? 'Revoke' : 'Grant'}
              </button>
            </form>
            {grantError && <p className="mt-2 text-sm text-blam">{grantError}</p>}
          </section>

          <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4 lg:col-span-2">
            <h2 className="font-semibold">Raids</h2>
            <table className="mt-2 w-full text-xs">
              <thead className="text-bruise">
                <tr>
                  <th className={th}>Issued</th>
                  <th className={th}>Character</th>
                  <th className={th}>Building</th>
                  <th className={th}>Status</th>
                  <th className={th}>Team</th>
                  <th className={th}>Mode</th>
                  <th className={th}>Loadout</th>
                  <th className={th}>Carried</th>
                  <th className={th}>Energy</th>
                  <th className={th}>Squad</th>
                </tr>
              </thead>
              <tbody>
                {(d.raids as RaidRow[]).map((r) => (
                  <tr
                    key={r.id}
                    className={`border-t border-newsprint align-top ${
                      r.status === 'lost' && r.rejectReason ? 'text-blam' : ''
                    }`}
                  >
                    <td className={`${td} font-mono`}>{new Date(r.issuedAt).toLocaleString()}</td>
                    <td className={td}>
                      {characterOf(r)
                        ? `${characterName(characterOf(r))} (slot ${characterOf(r)?.slot})`
                        : r.slot != null
                          ? `slot ${r.slot}`
                          : '-'}
                    </td>
                    <td className={td}>
                      {r.category} #{r.buildingId}
                    </td>
                    <td className={td}>
                      {r.status}
                      {r.rejectReason ? ` (${r.rejectReason})` : ''}
                      {r.devTools && (
                        <span
                          className="ml-1 rounded bg-caption px-1 font-bold"
                          title="Dev tools ticket"
                        >
                          DEV
                        </span>
                      )}
                    </td>
                    <td className={td}>{r.teamSlot != null ? `Team ${r.teamSlot}` : 'legacy'}</td>
                    <td className={td}>{r.mode || '-'}</td>
                    <td className={td}>{formatLoadout(r.loadout, itemName)}</td>
                    <td className={`${td} font-mono`}>{formatStacks(r.carried)}</td>
                    <td className={td}>{r.energyCost}</td>
                    <td className={td}>{squadSummary(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="rounded border-2 border-ink bg-white p-4">
            <h2 className="font-semibold">Recent locations</h2>
            <ul className="mt-2 space-y-1 font-mono text-xs">
              {d.recentPings.map((p) => (
                <li key={p.at} className={p.flag ? 'text-blam' : ''}>
                  {new Date(p.at).toLocaleString()} {p.lat.toFixed(5)},{p.lon.toFixed(5)}{' '}
                  {p.speedKmh?.toFixed(0) ?? '-'} km/h {p.flag ?? ''}
                </li>
              ))}
            </ul>
          </section>

          <PlayerBasesSection
            playerId={playerId}
            bases={d.bases ?? []}
            checkIns={d.checkIns ?? []}
          />

          <section className="rounded border-2 border-ink bg-white p-4 lg:col-span-2">
            <h2 className="font-semibold">Teams</h2>
            <TeamsPanel
              teams={d.teams}
              inUse={poolOf(d.inventory).inUse}
              kitTeamId={poolOf(d.inventory).kitTeamId}
              itemName={itemName}
            />
          </section>

          <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4 lg:col-span-2">
            <h2 className="font-semibold">Conditions</h2>
            <ConditionsTable rows={d.conditions} />
          </section>

          <PlayerSkillsSection playerId={playerId} />
          <PlayerEffectsSection playerId={playerId} />
          <PlayerActivitySection playerId={playerId} />
          <PlayerStorySection playerId={playerId} />
          <PlayerFeaturesSection playerId={playerId} />

          <PlayerStoreSection playerId={playerId} />
          <PlayerTriggerSection playerId={playerId} />

          <PlayerSavePoints playerId={playerId} playerLabel={d.player.displayName} />
        </div>
      </RunScopeContext.Provider>
    </>
  );
}
